/* eslint-disable max-lines */
import React from 'react';
import { PickerMode } from './types';
import { MONTHS } from './constants';
import { NavigationButton } from './styled';
import styled, { useTheme } from 'styled-components/macro';
import ArrowLeft from 'images/arrow';
import { fontSizePalette, fontWeightPalette, sizePalette } from 'utils/styleConstants';

interface CustomHeaderComponentProps {
  years: number[];
  pickerMode: PickerMode;
  onPickerModeChange: (mode: PickerMode) => void;
  date: Date;
  changeYear: (year: number) => void;
  changeMonth: (month: number) => void;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  isRange: boolean;
  onChange: (value: Date | null | [Date | null, Date | null]) => void;
  showQuickRangeButtons?: boolean;
  quickRangeType?: 'default' | 'report';
  setViewDate: (date: Date | undefined) => void;
}

const ModeButton = styled.button<{ selected?: boolean }>`
  width: 64px;
  font-family: ${(props) => props.theme.fontFamily};
  border: solid ${sizePalette.borderWidth.DEFAULT} transparent;
  background: transparent;
  font-size: ${fontSizePalette.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: ${sizePalette.letterSpacing.tight};
  font-weight: ${fontWeightPalette.medium};
  cursor: pointer;
  padding: 0;
  margin: 0;
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark};
  text-decoration: ${({ selected }) => (!selected ? 'underline' : 'none')};
  text-underline-offset: 6px;
  text-decoration-thickness: 2px;

  &:disabled {
    cursor: default;
    color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey8 : theme.colorPalette.grey.grey5};
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 8px;
`;

const NavigationRow = styled.div`
  display: grid;
  grid-template-columns: 30px 128fr 30px;
  align-items: center;
  justify-items: center;
  gap: 8px;
  width: 100%;
`;

const QuickRangeRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 16px 0 0 0;
`;

const QuickRangeButton = styled.button<{ selected?: boolean }>`
  font-family: ${(props) => props.theme.fontFamily};
  padding: 0 ${sizePalette.spacing[1]};
  border: solid ${sizePalette.borderWidth.DEFAULT};
  border-radius: ${sizePalette.borderRadius['2xl']};
  font-size: ${fontSizePalette.small};
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: ${sizePalette.letterSpacing.normal};
  font-weight: ${({ selected }) => (selected ? fontWeightPalette.medium : fontWeightPalette.regular)};
  line-height: ${sizePalette.lineHeight[5]};
  cursor: pointer;
  white-space: nowrap;
  height: ${sizePalette.spacing.pxToRem(26)};

  color: ${({ selected, theme }) =>
    selected
      ? theme.colorPalette.isDarkMode
        ? theme.colorPalette.white
        : theme.colorPalette.white
      : theme.colorPalette.isDarkMode
      ? theme.colorPalette.grey.grey10
      : theme.colorPalette.grey.grey9};

  border-color: ${({ selected, theme }) =>
    selected
      ? theme.colorPalette.isDarkMode
        ? theme.colorPalette.turquoise.accent
        : theme.colorPalette.turquoise.dark
      : theme.colorPalette.isDarkMode
      ? theme.colorPalette.grey.grey7
      : theme.colorPalette.grey.grey5};

  background: ${({ selected, theme }) =>
    selected
      ? theme.colorPalette.isDarkMode
        ? theme.colorPalette.turquoise.accent
        : theme.colorPalette.turquoise.dark
      : theme.colorPalette.isDarkMode
      ? 'transparent'
      : theme.colorPalette.white};
`;

const CustomHeaderComponent: React.FC<CustomHeaderComponentProps> = ({
  years,
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  pickerMode,
  onPickerModeChange,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  isRange,
  onChange,
  showQuickRangeButtons = true,
  quickRangeType = 'default',
  setViewDate,
}) => {
  const handleModeChange = (mode: PickerMode) => {
    onPickerModeChange(mode);
  };

  const handleQuickRange = (months: number) => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - months);

    if (start.getDate() !== end.getDate()) {
      start.setDate(1);
    }

    setStartDate(start);
    setEndDate(end);

    if (isRange) {
      onChange([start, end]);
    } else {
      onChange(start);
    }
    // Navigate to the start date's month/year
    changeYear(start.getFullYear());
    changeMonth(start.getMonth());
    setViewDate(end);
    onPickerModeChange('date');
  };

  const handleQuickRangeWeeks = (weeks: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - weeks * 7);

    setStartDate(start);
    setEndDate(end);

    if (isRange) {
      onChange([start, end]);
    } else {
      onChange(start);
    }
    changeYear(start.getFullYear());
    changeMonth(start.getMonth());
    setViewDate(end);
    onPickerModeChange('date');
  };

  const handleQuickRangeYears = (yearsNum: number) => {
    const end = new Date();
    const start = new Date();
    start.setFullYear(start.getFullYear() - yearsNum);

    setStartDate(start);
    setEndDate(end);

    if (isRange) {
      onChange([start, end]);
    } else {
      onChange(start);
    }
    changeYear(start.getFullYear());
    changeMonth(start.getMonth());
    setViewDate(end);
    onPickerModeChange('date');
  };

  const handleYTD = () => {
    const end = new Date();
    const start = new Date(end.getFullYear(), 0, 1); // January 1st of current year

    setStartDate(start);
    setEndDate(end);

    if (isRange) {
      onChange([start, end]);
    } else {
      onChange(start);
    }
    // Navigate to the start date's month/year
    changeYear(start.getFullYear());
    changeMonth(start.getMonth());
    setViewDate(end);
    onPickerModeChange('date');
  };

  const isRangeSelected = (months: number): boolean => {
    if (!startDate || !endDate) return false;

    const now = new Date();
    const expectedStart = new Date();
    expectedStart.setMonth(expectedStart.getMonth() - months);

    if (expectedStart.getDate() !== now.getDate()) {
      expectedStart.setDate(1);
    }

    // Check if dates match within the same day
    const isSameDay = (date1: Date, date2: Date) => {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    };

    return isSameDay(startDate, expectedStart) && isSameDay(endDate, now);
  };

  const isRangeSelectedWeeks = (weeks: number): boolean => {
    if (!startDate || !endDate) return false;

    const now = new Date();
    const expectedStart = new Date();
    expectedStart.setDate(expectedStart.getDate() - weeks * 7);

    const isSameDay = (date1: Date, date2: Date) => {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    };

    return isSameDay(startDate, expectedStart) && isSameDay(endDate, now);
  };

  const isRangeSelectedYears = (yearsNum: number): boolean => {
    if (!startDate || !endDate) return false;

    const now = new Date();
    const expectedStart = new Date();
    expectedStart.setFullYear(expectedStart.getFullYear() - yearsNum);

    const isSameDay = (date1: Date, date2: Date) => {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    };

    return isSameDay(startDate, expectedStart) && isSameDay(endDate, now);
  };

  const isYTDSelected = (): boolean => {
    if (!startDate || !endDate) return false;

    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);

    const isSameDay = (date1: Date, date2: Date) => {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    };

    return isSameDay(startDate, yearStart) && isSameDay(endDate, now);
  };

  const handlePrevious = () => {
    switch (pickerMode) {
      case 'year':
        {
          const newYearPrev = date.getFullYear() - 12;
          // Check if the new year is within the allowed range
          const minYear = years.length > 0 ? Math.min(...years) : 1900;
          const targetYear = Math.max(newYearPrev, minYear);
          changeYear(targetYear);
          const newDate = new Date(date);
          newDate.setFullYear(targetYear);
          setViewDate(newDate);
        }
        break;
      case 'month':
        {
          const newYearMonthPrev = date.getFullYear() - 1;
          const minYearMonth = years.length > 0 ? Math.min(...years) : 1900;
          const targetYear = Math.max(newYearMonthPrev, minYearMonth);
          changeYear(targetYear);
          const newDate = new Date(date);
          newDate.setFullYear(targetYear);
          setViewDate(newDate);
        }
        break;
      case 'date':
      default:
        decreaseMonth();
        {
          const newDate = new Date(date);
          newDate.setMonth(newDate.getMonth() - 1);
          setViewDate(newDate);
        }
        break;
    }
  };

  const handleNext = () => {
    switch (pickerMode) {
      case 'year':
        {
          const newYearNext = date.getFullYear() + 12;
          // Check if the new year is within the allowed range
          const maxYear = years.length > 0 ? Math.max(...years) : 2100;
          const targetYear = Math.min(newYearNext, maxYear);
          changeYear(targetYear);
          const newDate = new Date(date);
          newDate.setFullYear(targetYear);
          setViewDate(newDate);
        }
        break;
      case 'month':
        {
          const newYearMonthNext = date.getFullYear() + 1;
          const maxYearMonth = years.length > 0 ? Math.max(...years) : 2100;
          const targetYear = Math.min(newYearMonthNext, maxYearMonth);
          changeYear(targetYear);
          const newDate = new Date(date);
          newDate.setFullYear(targetYear);
          setViewDate(newDate);
        }
        break;
      case 'date':
      default:
        increaseMonth();
        {
          const newDate = new Date(date);
          newDate.setMonth(newDate.getMonth() + 1);
          setViewDate(newDate);
        }
        break;
    }
  };
  const theme = useTheme();

  const renderModebuttons = (pickerMode: PickerMode) => {
    if (pickerMode === 'year') {
      return <ModeButton selected={pickerMode === 'year'}>Year</ModeButton>;
    } else if (pickerMode === 'month') {
      return <ModeButton selected={pickerMode === 'month'}>Month</ModeButton>;
    }
    return (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        <ModeButton onClick={() => handleModeChange('month')}>{MONTHS[date.getMonth()]}</ModeButton>

        <ModeButton onClick={() => handleModeChange('year')}>{date.getFullYear()}</ModeButton>
      </div>
    );
  };
  return (
    <HeaderContainer id="custom-datepicker-header">
      <NavigationRow>
        <NavigationButton onClick={handlePrevious} disabled={prevMonthButtonDisabled}>
          <ArrowLeft
            rotation={0}
            color={
              theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark
            }
          />
        </NavigationButton>

        {renderModebuttons(pickerMode)}

        <NavigationButton onClick={handleNext} disabled={nextMonthButtonDisabled}>
          <ArrowLeft
            rotation={180}
            color={
              theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark
            }
          />
        </NavigationButton>
      </NavigationRow>
      {showQuickRangeButtons && (
        <QuickRangeRow>
          {quickRangeType === 'default' ? (
            <>
              <QuickRangeButton onClick={() => handleQuickRange(1)} selected={isRangeSelected(1)}>
                1 Month
              </QuickRangeButton>
              <QuickRangeButton onClick={() => handleQuickRange(3)} selected={isRangeSelected(3)}>
                3 Months
              </QuickRangeButton>
              <QuickRangeButton onClick={() => handleQuickRange(6)} selected={isRangeSelected(6)}>
                6 Months
              </QuickRangeButton>
              <QuickRangeButton onClick={handleYTD} selected={isYTDSelected()}>
                YTD
              </QuickRangeButton>
            </>
          ) : (
            <>
              <QuickRangeButton onClick={() => handleQuickRangeWeeks(1)} selected={isRangeSelectedWeeks(1)}>
                1 Week
              </QuickRangeButton>
              <QuickRangeButton onClick={() => handleQuickRange(1)} selected={isRangeSelected(1)}>
                1 Month
              </QuickRangeButton>
              <QuickRangeButton onClick={() => handleQuickRange(3)} selected={isRangeSelected(3)}>
                3 Months
              </QuickRangeButton>
              <QuickRangeButton onClick={() => handleQuickRangeYears(1)} selected={isRangeSelectedYears(1)}>
                1 Year
              </QuickRangeButton>
            </>
          )}
        </QuickRangeRow>
      )}
    </HeaderContainer>
  );
};

export default CustomHeaderComponent;
